import React from "react";
import { BORDER_STRONG, CONTROL, DESTRUCTIVE, RADIUS_INPUT, TEAL, TEXT, TEXT_MUTED } from "./tokens";

/** 48px tall, 16px text (no iOS auto-zoom), 12px radius. Spread onto any <input>/<select>. */
export const inputStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  height: `${CONTROL}px`,
  boxSizing: "border-box",
  padding: "0 14px",
  fontSize: "16px",
  lineHeight: "24px",
  color: TEXT,
  backgroundColor: "#FFFFFF",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: BORDER_STRONG,
  borderRadius: RADIUS_INPUT,
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

/** The focused / invalid variants of `inputStyle`. */
export const inputFocusStyle: React.CSSProperties = { borderColor: TEAL, boxShadow: `0 0 0 2px ${TEAL}1F` };
export const inputErrorStyle: React.CSSProperties = { borderColor: DESTRUCTIVE };

/** Label ABOVE a field: 13px/500 muted, 6px below. */
export const FieldLabel: React.FC<{ htmlFor?: string; children: React.ReactNode; required?: boolean }> = ({
  htmlFor,
  children,
  required,
}) => (
  <label
    htmlFor={htmlFor}
    className="font-inter"
    style={{ display: "block", fontSize: "13px", fontWeight: 500, color: TEXT_MUTED, marginBottom: "6px" }}
  >
    {children}
    {required && <span style={{ color: DESTRUCTIVE }}> *</span>}
  </label>
);

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}

/**
 * Text input with the kit's 48px/16px/12px geometry. Pass `inputMode`
 * ("numeric" / "decimal") and `type` ("email" / "tel") as on a plain input.
 * `error` renders the inline message under the field and the red border.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(({ error, style, onFocus, onBlur, ...rest }, ref) => {
  const [focused, setFocused] = React.useState(false);
  return (
    <>
      <input
        ref={ref}
        className="font-inter placeholder:text-placeholder"
        aria-invalid={!!error || undefined}
        {...rest}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        style={{
          ...inputStyle,
          ...(focused ? inputFocusStyle : {}),
          ...(error ? inputErrorStyle : {}),
          ...style,
        }}
      />
      {error && <FieldError>{error}</FieldError>}
    </>
  );
});
Input.displayName = "MobileInput";

/** Inline validation message under a field (13px, destructive). */
export const FieldError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="font-inter" role="alert" style={{ marginTop: "6px", fontSize: "13px", color: DESTRUCTIVE }}>
    {children}
  </p>
);

/** Label + input + error as one block with 16px below. */
export const Field: React.FC<InputProps & { label: string; required?: boolean }> = ({ label, required, id, ...rest }) => {
  const generated = React.useId();
  const inputId = id ?? `field-${generated}`;
  return (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel htmlFor={inputId} required={required}>
        {label}
      </FieldLabel>
      <Input id={inputId} {...rest} />
    </div>
  );
};
